/**
 * Step 3 · CRS & Boundary
 *
 * The user can:
 *   • confirm/override the auto-suggested EPSG (from country + lat/lon)
 *   • upload a KML/KMZ/SHP/GeoJSON boundary (delegated to the KML studio API)
 *   • preview the boundary before "Approving as Project Boundary"
 *
 * Boundary upload/approval is handled by /api/projects/{pid}/gis/*.
 * Detailed layer-selector + viewer is Sprint 2 (Priority 2). This step
 * exposes the essential upload + approve flow so a project can be
 * created with an approved boundary from day one.
 */
import { useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Globe, MapPin, UploadSimple, CheckCircle, CircleNotch, X, Info,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { FieldRow, StepHeader } from "./Step1Identity";

export default function Step3CRSBoundary({ form, set, draftId, ensureDraft }) {
  const [uploading, setUploading] = useState(false);

  // Suggest EPSG from country + lat/lon (delegates to backend)
  const crsSuggestQ = useQuery({
    queryKey: ["crs-suggest", form.country, form.latitude, form.longitude],
    queryFn: async () => {
      if (form.latitude != null && form.longitude != null) {
        const r = await api.post("/setup/recommend-crs", {
          lat_min: form.latitude, lat_max: form.latitude,
          lon_min: form.longitude, lon_max: form.longitude,
        });
        return r.data;
      }
      if (form.country) {
        const r = await api.get(`/setup/country/${form.country}`);
        const zones = r.data?.profile?.utm_zones || [];
        if (zones[0]) {
          return { epsg: zones[0].epsg, utm_zone: zones[0].zone,
                    hemisphere: zones[0].hemi, source: "country_default" };
        }
      }
      return null;
    },
    enabled: Boolean(form.country || (form.latitude != null && form.longitude != null)),
  });

  // Boundary present on the draft?
  const boundaryQ = useQuery({
    queryKey: ["boundary", draftId],
    enabled: Boolean(draftId),
    queryFn: () => api.get(`/setup/draft/${draftId}/status`).then((r) => r.data.boundary),
  });

  const suggestion = crsSuggestQ.data;
  const boundary   = boundaryQ.data;

  const acceptSuggestion = useCallback(() => {
    if (!suggestion) return;
    set({
      epsg:       suggestion.epsg,
      utm_zone:   suggestion.utm_zone,
      hemisphere: suggestion.hemisphere,
      crs_approved: true,
    });
    toast.success("CRS suggestion accepted");
  }, [suggestion, set]);

  const uploadBoundary = useMutation({
    mutationFn: async (file) => {
      let pid = draftId;
      if (!pid) pid = (await ensureDraft()).draft_project_id;
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/projects/${pid}/gis/boundary/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return r.data;
    },
    onSuccess: () => {
      toast.success("Boundary uploaded — preview in Step 4.");
      boundaryQ.refetch();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });

  const onFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { await uploadBoundary.mutateAsync(file); } finally {
      setUploading(false); e.target.value = "";
    }
  }, [uploadBoundary]);

  return (
    <section className="space-y-4" data-testid="step-3-crs-boundary">
      <StepHeader step="Step 3" title="CRS & Boundary"
        subtitle="Confirm the coordinate reference system and upload the licence boundary. AI suggests an EPSG code based on country + coordinates; you can override at any time." />

      {/* CRS block */}
      <div className="rounded-lg bg-white border border-slate-200 divide-y divide-slate-100">
        <FieldRow icon={Globe} label="EPSG Code" required
                     hint="Numeric EPSG identifier — e.g. 32736 for UTM Zone 36S.">
          <div className="flex items-center gap-3">
            <input type="number" value={form.epsg ?? ""}
                     onChange={(e) => set({ epsg: e.target.value === "" ? undefined : Number(e.target.value) })}
                     data-testid="input-epsg"
                     className="w-40 h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px] font-mono" />
            {suggestion && suggestion.epsg !== form.epsg && (
              <button type="button" onClick={acceptSuggestion}
                          data-testid="btn-accept-crs-suggestion"
                          className="inline-flex items-center gap-1.5 rounded-md border border-brand-blue/30 bg-brand-blue/5 hover:bg-brand-blue/10 px-2.5 h-11 text-[12.5px] text-brand-blue font-medium">
                <CircleNotch size={12} weight="fill" />
                AI suggests <b className="font-mono ml-1">{suggestion.epsg}</b>
                {suggestion.source && <span className="text-[10px] uppercase tracking-widest opacity-70 ml-1">
                  {suggestion.source.replaceAll("_"," ")}
                </span>}
              </button>
            )}
          </div>
        </FieldRow>

        <FieldRow icon={MapPin} label="UTM Zone / Hemisphere">
          <div className="grid grid-cols-2 gap-2">
            <input type="number" value={form.utm_zone ?? ""}
                     onChange={(e) => set({ utm_zone: e.target.value === "" ? undefined : Number(e.target.value) })}
                     placeholder="Zone (1–60)"
                     data-testid="input-utm-zone"
                     className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px] font-mono" />
            <select value={form.hemisphere || ""}
                    onChange={(e) => set({ hemisphere: e.target.value })}
                    data-testid="select-hemisphere"
                    className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]">
              <option value="">— Hemisphere —</option>
              <option value="N">Northern (N)</option>
              <option value="S">Southern (S)</option>
            </select>
          </div>
        </FieldRow>

        <FieldRow icon={Globe} label="Horizontal Datum">
          <input type="text" value={form.horizontal_datum || "WGS84"}
                   onChange={(e) => set({ horizontal_datum: e.target.value })}
                   data-testid="input-horizontal-datum"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>

        <FieldRow icon={Globe} label="Vertical Datum" hint="Optional — e.g. EGM2008, MSL, local benchmark.">
          <input type="text" value={form.vertical_datum || ""}
                   onChange={(e) => set({ vertical_datum: e.target.value })}
                   placeholder="e.g. EGM2008"
                   data-testid="input-vertical-datum"
                   className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-[14px]" />
        </FieldRow>
      </div>

      {/* Boundary block */}
      <div className="rounded-lg bg-white border border-slate-200 p-4 space-y-3" data-testid="boundary-block">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
            <UploadSimple size={14} className="text-slate-500" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[13.5px] text-slate-900">Project Boundary</div>
            <div className="text-[11px] text-slate-500">
              Upload a KML, KMZ, SHP (ZIP) or GeoJSON. You can preview and approve in Step 4 or later from the GIS Studio.
            </div>
          </div>
          {boundary ? (
            <span data-testid="boundary-loaded" className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5 text-[10.5px] uppercase tracking-widest">
              <CheckCircle size={10} weight="fill" /> Loaded · {boundary.area_km2 ? `${Number(boundary.area_km2).toFixed(2)} km²` : boundary.filename || "boundary"}
            </span>
          ) : (
            <span data-testid="boundary-missing" className="inline-flex items-center gap-1 text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-1.5 py-0.5 text-[10.5px] uppercase tracking-widest">
              Optional
            </span>
          )}
        </div>
        <label className="block">
          <input type="file"
                    accept=".kml,.kmz,.geojson,.json,.zip,.shp"
                    onChange={onFile}
                    disabled={uploading}
                    data-testid="input-boundary-file"
                    className="w-full text-[12px] file:mr-3 file:h-10 file:rounded-md file:border-0 file:bg-brand-blue file:text-white file:px-3 file:text-[11.5px] file:font-semibold hover:file:bg-brand-blue/90" />
        </label>
        {uploading && (
          <div className="text-[11.5px] text-slate-500 flex items-center gap-1">
            <CircleNotch size={11} className="animate-spin" /> Uploading…
          </div>
        )}
      </div>

      <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-[11.5px] text-slate-600 flex items-start gap-2">
        <Info size={11} className="text-slate-400 mt-0.5" />
        <div className="flex-1">
          Full boundary preview + layer selector + collar-vs-boundary validation is available in the <b>GIS Studio</b> after project creation. This step gets you a working CRS + uploaded boundary quickly.
        </div>
      </div>
    </section>
  );
}
